import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type { StoreFrontDto } from '@b-stock/listing-api-client'
import type { GetAllListingsDto } from '@b-stock/search-api-client'

import type {
  ListingSearchScope,
  ListingSearchState,
} from '@/components/listings/search/ListingSearchProvider'
import { listingSearchStateToSearchParams } from '@/components/listings/search/ListingSearchProvider/utils'
import getAllListingsSearchClient from '@/queries/getAllListingsSearchClient'

// AllListingsApiAllListingsRequest.limit doc comment: max 200 per request.
const CHUNK_SIZE = 200

/**
 * Mirrors order-process's own default (MANIFEST_EXPORT_MAX_LISTING_IDS in
 * OrderProcessConfig) so the frontend fails fast instead of making a
 * request the backend will reject anyway. This is a client-side optimization,
 * not the source of truth — the backend enforces its own cap independently
 * (and may be tuned via env var without this constant changing), so a
 * mismatch fails safe: `useDownloadAllManifests` also handles a real
 * EXPORT_CAP_EXCEEDED rejection from the backend itself, not just this
 * pre-check.
 */
export const MAX_MANIFEST_EXPORT_LISTINGS = 1_500

export class ManifestExportCapExceededError extends Error {
  constructor(
    public readonly total: number,
    public readonly cap: number
  ) {
    super(`Filtered result set (${total}) exceeds export cap (${cap})`)
    this.name = 'ManifestExportCapExceededError'
  }
}

/**
 * Resolves every listing ID matching the current filter/search state, across
 * all pages — not just what's currently loaded in the grid. Bypasses
 * `getSearchData`'s bid/auction/offering augmentation (three extra requests
 * per chunk) since only `listingId` is needed here.
 *
 * Fails fast with `ManifestExportCapExceededError` off the very first chunk's
 * `total` if the filtered set is too large, instead of paging through the
 * rest of it first.
 */
export async function fetchAllFilteredListingIds(
  accessToken: string | null,
  state: ListingSearchState,
  storefront: StoreFrontDto | undefined,
  {
    cap = MAX_MANIFEST_EXPORT_LISTINGS,
    chunkSize = CHUNK_SIZE,
    scope,
  }: { cap?: number; chunkSize?: number; scope?: ListingSearchScope } = {}
): Promise<{ listingIds: string[]; total: number }> {
  const searchClient = getAllListingsSearchClient()
  const axiosParams = buildAxiosRequestConfig({ accessToken })

  // `query` mirrors getSearchData's own destructure (listingSearchQuery.ts):
  // it's a GET-endpoint-only field not present on the POST DTO, and must be
  // stripped rather than spread into `getAllListingsDto`.
  const { query: _query, ...searchParams } = listingSearchStateToSearchParams(
    { ...state, page: 1 },
    storefront,
    scope
  )

  const listingIds = new Set<string>()
  let offset = 0
  let total = Infinity

  while (offset < total) {
    const getAllListingsDto: GetAllListingsDto = {
      ...searchParams,
      // Paper over 'deals' type mismatch, same cast getSearchData applies.
      deals: searchParams.deals,
      limit: chunkSize,
      offset,
    }

    const { data } = await searchClient.allListingsPost(
      { getAllListingsDto },
      axiosParams
    )

    total = data.total

    if (total > cap) {
      throw new ManifestExportCapExceededError(total, cap)
    }

    if (!data.listings.length) break // safety net against an infinite loop

    // Advance by rows returned, not chunkSize: text searches (`q`) can return
    // fewer rows per call than `limit`.
    // Rows the search service drops after paging shorten a page, so the next
    // one can overlap it; the Set prevents duplicates.
    data.listings.forEach((listing) => listingIds.add(listing.listingId))
    offset += data.listings.length
  }

  return { listingIds: [...listingIds], total }
}
